<template>
    <div class="group-progress-category-overview">
        <div class="group-progress-category-filer">
            <div class="categories">
                <b-form-select
                    class="apprentice-primary-select group-progress-category-filter-select"
                    v-model="selectedCategoryFilter"
                    :options="getCategoryOptions"
                    @change="updateCategoryFilter">
                </b-form-select>
            </div>

            <div class="assignments">
                <b-form-select
                    class="apprentice-primary-select group-progress-assignments-filter-select"
                    v-model="selectedAssignmentsFilter"
                    :disabled="selectedCategoryFilter === null"
                    :options="getAssignmentsOptions"
                    @change="updateAssignmentFilter">
                </b-form-select>
            </div>

            <div class="sub-assignments">
                <b-form-select
                    class="apprentice-primary-select group-progress-subassignments-filter-select"
                    v-model="selectedSubAssignmentsFilter"
                    :disabled="selectedAssignmentsFilter === null"
                    :options="getSubAssignmentsOptions">
                </b-form-select>
            </div>

            <div
                class="approve-all"
                v-if="selectedAssignmentsFilter">
                <b-button
                    class="btn-checked btn-approve-all"
                    variant="primary"
                    @click="closeAll()">
                    alles goedkeuren
                </b-button>
            </div>
        </div>

        <design-2021-group-progress-category-overview-all-categories-component
            v-if="!selectedCategoryFilter"
            :has-children="groupIn.children.length > 0"
            :group-data-in="groupDataIn"
            :selected-status-in="selectedStatusIn"
            :category-options="getCategoryOptions"
            :group-in="groupIn"
            :search-term-in="searchTermIn"
            :student-per-projects-in="studentPerProjectsIn">
        </design-2021-group-progress-category-overview-all-categories-component>

        <design-2021-group-progress-category-overview-all-assignments-component
            v-else-if="selectedCategoryFilter && !selectedAssignmentsFilter"
            :has-children="groupIn.children.length > 0"
            :group-data-in="groupDataIn"
            :selected-status-in="selectedStatusIn"
            :assignments-options="getAssignmentsOptions"
            :group-in="groupIn"
            :search-term-in="searchTermIn"
            :student-per-projects-in="studentPerProjectsIn"
            :selected-category-filter="selectedCategoryFilter">
        </design-2021-group-progress-category-overview-all-assignments-component>

        <design-2021-group-progress-category-overview-all-sub-assignments-component
            v-else-if="selectedAssignmentsFilter && !selectedSubAssignmentsFilter"
            :has-children="groupIn.children.length > 0"
            :group-data-in="groupDataIn"
            :selected-status-in="selectedStatusIn"
            :sub-assignments-options="getSubAssignmentsOptions"
            :group-in="groupIn"
            :search-term-in="searchTermIn"
            :student-per-projects-in="studentPerProjectsIn"
            :selected-assignments-filter="selectedAssignmentsFilter"
            @updateGroupData="updateGroupData">
        </design-2021-group-progress-category-overview-all-sub-assignments-component>

        <design-2021-group-progress-category-overview-selected-sub-assignment-component
            v-else
            :has-children="groupIn.children.length > 0"
            :group-data-in="groupDataIn"
            :selected-status-in="selectedStatusIn"
            :sub-assignments-options="getSubAssignmentsOptions"
            :group-in="groupIn"
            :search-term-in="searchTermIn"
            :student-per-projects-in="studentPerProjectsIn"
            :selected-sub-assignment="selectedSubAssignmentsFilter"
            @updateGroupData="updateGroupData">
        </design-2021-group-progress-category-overview-selected-sub-assignment-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-group-progress-category-overview-component',
    props: {
        groupDataIn: {
            type: Object,
            required: true,
        },
        groupIn: {
            type: Object,
            required: true,
        },
        selectedStatusIn: {
            type: Array,
            required: true,
        },
        searchTermIn: {
            type: String,
            required: false,
            default: '',
        },
        studentPerProjectsIn: {
            type: Array,
            required: false,
            default: [],
        },
    },

    data() {
        return {
            selectedCategoryFilter: null,
            selectedAssignmentsFilter: null,
            selectedSubAssignmentsFilter: null,
        };
    },

    computed: {
        fields() {
            let fields = [
                {
                    key: 'name',
                    label: this.$t('common.first_name'),
                    sortable: true,
                },
                {
                    key: 'surname',
                    label: this.$t('common.last_name'),
                    sortable: true,
                },
            ];

            if (this.groupIn.children.length > 0) {
                fields.push({
                    key: 'sub_project',
                    label: this.$t('group.classes'),
                    sortable: true,
                });
            }

            if (this.selectedCategoryFilter === null) {
                for (const category of this.getCategoryOptions) {
                    if (category.value === null) {
                        continue;
                    }

                    fields.push({
                        key: `category-${category.text.toLowerCase()}`,
                        label: category.text,
                    });
                }
            } else {
                for (const assignment of this.getAssignmentsOptions) {
                    if (assignment.value === null) {
                        continue;
                    }

                    fields.push({
                        key: `assignment-${assignment.value}`,
                        label: assignment.text,
                    });
                }
            }

            fields.push({
                key: 'last_submitted',
                label: this.$t('group.last_submitted'),
                sortable: true,
            });

            fields.push({
                key: 'last_updated',
                label: this.$t('group.last_modified'),
                sortable: true,
            });

            return fields;
        },

        getCategoryOptions() {
            let categoryOptions = this.groupDataIn.portfolio.categories.map((category) => {
                return {
                    value: category.id,
                    text: category.name,
                };
            });

            categoryOptions.unshift({
                value: null,
                text: this.$t('group.all_categories'),
            });

            return categoryOptions;
        },

        getAssignmentsOptions() {
            let assignments = [
                {
                    value: null,
                    text: this.$t('group.all_categories'),
                },
            ];

            if (this.selectedCategoryFilter === null) {
                return assignments;
            }

            for (const category of this.groupDataIn.portfolio.categories) {
                if (category.id !== this.selectedCategoryFilter) {
                    continue;
                }

                for (const assignment of category.assignments) {
                    assignments.push({
                        value: assignment.id,
                        text: assignment.name,
                    });
                }

                break;
            }

            return assignments;
        },

        getSubAssignmentsOptions() {
            let subAssignments = [
                {
                    value: null,
                    text: this.$t('group.all_partial_exercises'),
                },
            ];

            if (this.selectedAssignmentsFilter === null) {
                return subAssignments;
            }

            for (const category of this.groupDataIn.portfolio.categories) {
                if (category.id !== this.selectedCategoryFilter) {
                    continue;
                }

                for (const assignment of category.assignments) {
                    if (assignment.id !== this.selectedAssignmentsFilter) {
                        continue;
                    }

                    for (const subAssignment of assignment.sub_assignments) {
                        subAssignments.push({
                            value: subAssignment.id,
                            text: subAssignment.name,
                        });
                    }
                    break;
                }

                break;
            }

            return subAssignments;
        },

        items() {
            if (this.groupDataIn.assignments.sub_assignments.length === 0) {
                return [];
            }

            if (this.selectedCategoryFilter === null) {
                return this.getCategoriesProgressData('assignments');
            } else {
                return this.getCategoryAssignmentsProgressData();
            }
        },
    },

    methods: {
        updateCategoryFilter(selected) {
            if (selected === null || (selected !== null && this.selectedCategoryFilter !== null)) {
                this.selectedAssignmentsFilter = null;
                this.selectedSubAssignmentsFilter = null;
            }
        },

        updateAssignmentFilter(selected) {
            if (selected === null || (selected !== null && this.selectedAssignmentsFilter !== null)) {
                this.selectedSubAssignmentsFilter = null;
            }
        },

        getCategoriesProgressData(filter) {
            let groupProgressData = [];
            let projectIdArray = this.studentPerProjectsIn.map((project) => project.project_id);
            for (const category of this.groupDataIn.categories[filter]) {
                if (projectIdArray.includes(category.project_id)) {
                    let categories = [];

                    categories.id = category.project_id;
                    categories.name = category.user.name;
                    categories.surname = category.user.surname;
                    categories.user_id = category.user.id;
                    categories.assignments = [];
                    categories.last_updated = category.last_updated;
                    categories.last_submitted = category.last_submitted;

                    for (const cat of category.categories) {
                        let assignment = {
                            id: cat.id,
                            name: cat.name,
                            statuses: {
                                closed: cat.statuses.closed,
                                submitted: cat.statuses.submitted,
                                reopened: cat.statuses.reopened,
                                open: cat.statuses.open,
                            },
                            total: cat.statuses.total,
                        };

                        categories.assignments.push(assignment);
                    }

                    groupProgressData.push({
                        ...categories,
                    });
                }
            }

            return this.getFilteredCategoryProgressData(groupProgressData);
        },

        getFilteredCategoryProgressData(rawData) {
            const filteredData = rawData;

            if (!this.selectedStatusIn.length) {
                return [];
            }

            const submitted = this.selectedStatusIn.includes('submitted');
            const reopened = this.selectedStatusIn.includes('reopened');
            const closed = this.selectedStatusIn.includes('closed');
            const open = this.selectedStatusIn.includes('open');

            let filteredStudents = [];

            for (const data of filteredData) {
                for (let assignment of data.assignments) {
                    let total = 0;
                    let status = [];
                    let filteredStatus = {};

                    if (submitted && assignment.statuses.submitted !== 0) {
                        filteredStatus.submitted = assignment.statuses.submitted;
                        total += assignment.statuses.submitted;
                    }

                    if (reopened && assignment.statuses.reopened !== 0) {
                        filteredStatus.reopened = assignment.statuses.reopened;
                        total += assignment.statuses.reopened;
                    }

                    if (closed && assignment.statuses.closed !== 0) {
                        filteredStatus.closed = assignment.statuses.closed;
                        total += assignment.statuses.closed;
                    }

                    if (open && assignment.statuses.open !== 0) {
                        filteredStatus.open = assignment.statuses.open;
                        total += assignment.statuses.open;
                    }

                    assignment.total = total;
                    assignment.statuses = { ...filteredStatus };
                }

                if (data.assignments.length !== 0) {
                    filteredStudents.push(data);
                }
            }

            filteredStudents = filteredStudents.filter((student) => {
                student.assignments = student.assignments.filter((assignment) => {
                    return (
                        assignment.statuses &&
                        Object.keys(assignment.statuses).length !== 0 &&
                        Object.getPrototypeOf(assignment.statuses) === Object.prototype
                    );
                });

                return student.assignments.length > 0;
            });

            return filteredStudents;
        },

        getCategoryAssignmentsProgressData() {
            let assignmentsData = this.groupDataIn.assignments.sub_assignments.map((subAssignment) => {
                let statuses = {
                    name: subAssignment.user.name,
                    surname: subAssignment.user.surname,
                    user_id: subAssignment.user.id,
                    id: subAssignment.project_id,
                    last_updated: subAssignment.last_updated,
                    last_submitted: subAssignment.last_submitted,
                    assignments: [],
                };

                for (const assignment of subAssignment.assignments) {
                    if (assignment.category_id === this.selectedCategoryFilter) {
                        let new_assignment = {
                            id: assignment.id,
                            name: assignment.name,
                            statuses: {
                                closed: assignment.statuses.closed,
                                submitted: assignment.statuses.submitted,
                                reopened: assignment.statuses.reopened,
                                open: assignment.statuses.open,
                            },
                            total: assignment.statuses.total,
                        };

                        statuses.assignments.push(new_assignment);
                    }
                }
                return statuses;
            });

            return this.getFilteredCategoryProgressData(assignmentsData);
        },

        viewRoute(group) {
            switch (this.groupIn.type_id) {
                case 1:
                    return this.routes.paperShow(group.id);
                case 5:
                    return this.routes.paperShow(group.id);
                case 2:
                    return this.routes.portfolioShow(group.id);
                case 4:
                    return this.routes.magazineShow(group.id);
            }
        },

        getStudentSubProjectName(data) {
            for (const student of this.studentPerProjectsIn) {
                if (student.project_id === data.id && student.id === data.user_id) {
                    return student.groups.join(', ');
                }
            }
        },

        openAssignmentCategory(project_id, category_id) {
            window.location.href =
                this.routes.baseUrl() + this.routes.portfolioShow(project_id) + '?category_id=' + category_id;
        },

        viewAssignment(project_id, assignment_id) {
            window.location.href = this.routes.baseUrl() + this.routes.assignmentShow(assignment_id, project_id);
        },

        getToolTipLabel(number, label) {
            switch (label) {
                case 'closed':
                    return `${number} ${this.$t('group.completed')}`;

                case 'submitted':
                    return `${number} ${this.$t('group.submitted')}`;

                case 'reopened':
                    return `${number} ${this.$t('group.reopened')}`;

                case 'open':
                    return `${number} ${this.$t('common.open')}`;
            }
        },

        categoryCell(key) {
            return `cell(category-${key.toLowerCase()})`;
        },

        assignmentCell(key) {
            if (key.value !== null) {
                return `cell(assignment-${key.value})`;
            }
        },

        closeAll() {
            if (this.selectedSubAssignmentsFilter) {
                this.alert
                    .confirm(
                        this.$t('group.approve_sub_assignment'),
                        this.$t('group.confirm_approve_sub_assignment'),
                        this.$t('group.approve'),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }

                        this.alert.saving();
                        let data = {
                            group_id: this.groupIn.id,
                            sub_assignment_id: this.selectedSubAssignmentsFilter,
                        };

                        this.axios.post(this.routes.subAssignmentStatusCloseAll(), data).then(
                            (response) => {
                                this.updateGroupData();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    });
            } else {
                this.alert
                    .confirm(
                        this.$t('group.approve_assignment'),
                        this.$t('group.confirm_approve_assignment'),
                        this.$t('group.approve'),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }

                        this.alert.saving();
                        let data = {
                            group_id: this.groupIn.id,
                            assignment_id: this.selectedAssignmentsFilter,
                        };

                        this.axios.post(this.routes.assignmentStatusCloseAll(), data).then(
                            (response) => {
                                this.updateGroupData();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    });
            }
        },

        updateGroupData() {
            this.$emit('updateGroupData');
        },
    },
};
</script>

<style scoped lang="scss">
.group-progress-overview-table ::v-deep {
    thead {
        select {
            margin-bottom: 0;
        }
    }
}

.group-progress-category-overview {
    padding-top: 9px;
}

.group-progress-category-filer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: $grid-gutter-width;
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    select {
        margin-bottom: 0;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .group-progress-category-filer {
        select {
            max-width: 250px;
        }
    }
}
</style>
